<template>
  <div class="chart-container">
    <div v-if="error" class="alert alert-danger">
      {{ error }}
    </div>
    <div v-else-if="!chartData" class="chart-loading-placeholder">
      {{ $t("common.loading") }}
    </div>
    <canvas v-else ref="chartCanvas"></canvas>
  </div>
</template>

<script setup lang="ts">
import Chart from "@/chart-setup";
import { PALETTE } from "@/composables/chartPalette";
import { type ChartConfiguration } from "chart.js";
import {
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  useTemplateRef,
  watch,
} from "vue";
import type {
  MarketPriceData,
  TransactionPerformanceData,
} from "./transactionPerformanceApi";

interface Props {
  marketPrices: MarketPriceData[];
  transactions: TransactionPerformanceData[];
}

const props = defineProps<Props>();

const chartCanvas = useTemplateRef<HTMLCanvasElement>("chartCanvas");
const chartInstance = ref<Chart<"line"> | null>(null);
const chartData = ref<ChartConfiguration<"line"> | null>(null);
const error = ref<string | null>(null);

const destroyChart = () => {
  if (chartInstance.value) {
    chartInstance.value.destroy();
    chartInstance.value = null;
  }
};

const buildChartData = (): ChartConfiguration<"line"> | null => {
  // Build market price line dataset
  const marketPriceLabels: string[] = [];
  const marketPriceData: (number | null)[] = [];

  props.marketPrices.forEach((price) => {
    const dateStr = price.date.split("T")[0];
    marketPriceLabels.push(dateStr);
    marketPriceData.push(price.bid !== null ? parseFloat(price.bid) : null);
  });

  // Build transaction scatter datasets
  const standardSalesData: { x: string; y: number; id: number }[] = [];
  const forwardSalesData: { x: string; y: number; id: number }[] = [];

  props.transactions.forEach((transaction) => {
    const dateStr = transaction.transaction_at.split("T")[0];
    const price = parseFloat(transaction.unit_price);

    const point = { x: dateStr, y: price, id: transaction.id };

    if (transaction.is_forward) {
      forwardSalesData.push(point);
    } else {
      standardSalesData.push(point);
    }
  });

  // Combine all dates for labels
  const allDates = new Set<string>();
  marketPriceLabels.forEach((d) => allDates.add(d));
  standardSalesData.forEach((p) => allDates.add(p.x));
  forwardSalesData.forEach((p) => allDates.add(p.x));

  const sortedDates = Array.from(allDates).sort();

  // Map market prices to sorted dates
  const marketPriceMap = new Map<string, number | null>();
  marketPriceLabels.forEach((date, idx) => {
    marketPriceMap.set(date, marketPriceData[idx] ?? null);
  });

  // Build market line data aligned to sorted dates
  const marketLineData: (number | null)[] = [];
  sortedDates.forEach((date) => {
    marketLineData.push(marketPriceMap.get(date) ?? null);
  });

  const datasets: any[] = [
    {
      label: "GO - Nordic Hydro BID",
      data: marketLineData,
      borderColor: PALETTE.role.reference,
      backgroundColor: PALETTE.role.reference + "1A",
      fill: false,
      tension: 0.1,
      pointRadius: 0,
      pointHoverRadius: 4,
      borderWidth: 2,
      spanGaps: true,
    },
  ];

  // Add included standard sales transactions scatter
  if (standardSalesData.length > 0) {
    datasets.push({
      label: "Spot Sales",
      data: standardSalesData,
      backgroundColor: PALETTE.semanticOverrides.portfolioType["Spot Sales"],
      borderColor: PALETTE.semanticOverrides.portfolioType["Spot Sales"],
      pointRadius: 6,
      pointHoverRadius: 8,
      pointBackgroundColor:
        PALETTE.semanticOverrides.portfolioType["Spot Sales"],
      pointBorderColor: "#fff",
      pointBorderWidth: 2,
      showLine: false,
      parsing: {
        xAxisKey: "x",
        yAxisKey: "y",
      },
    });
  }

  // Add included forward sales transactions scatter
  if (forwardSalesData.length > 0) {
    datasets.push({
      label: "Forward Sales",
      data: forwardSalesData,
      backgroundColor: PALETTE.semanticOverrides.portfolioType.Forward,
      borderColor: PALETTE.semanticOverrides.portfolioType.Forward,
      pointRadius: 6,
      pointHoverRadius: 8,
      pointBackgroundColor: PALETTE.semanticOverrides.portfolioType.Forward,
      pointBorderColor: "#fff",
      pointBorderWidth: 2,
      showLine: false,
      parsing: {
        xAxisKey: "x",
        yAxisKey: "y",
      },
    });
  }

  return {
    type: "line",
    data: {
      labels: sortedDates,
      datasets,
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: "nearest",
        axis: "xy",
        intersect: false,
      },
      plugins: {
        legend: {
          display: true,
          position: "top",
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              if (context.datasetIndex === 0) {
                // Market price line
                return `Market BID: €${context.parsed.y?.toFixed(4) || "N/A"}/MWh`;
              } else {
                // Transaction scatter
                const transaction = props.transactions.find(
                  (t) => t.id === context.raw.id
                );
                if (transaction) {
                  const delta =
                    transaction.reference_market_price &&
                    transaction.delta_vs_market
                      ? ` (Δ ${parseFloat(transaction.delta_vs_market).toFixed(4)} €/MWh)`
                      : "";
                  return `Transaction #${transaction.id}: €${transaction.unit_price}/MWh${delta}`;
                }
                return "";
              }
            },
            afterLabel: (context: any) => {
              if (context.datasetIndex > 0) {
                // Transaction scatter - show additional info
                const transaction = props.transactions.find(
                  (t) => t.id === context.raw.id
                );
                if (transaction) {
                  return [
                    `Client: ${transaction.counterparty_name || "N/A"}`,
                    `Volume: ${transaction.amount} MWh`,
                    transaction.reference_market_price
                      ? `Market: €${transaction.reference_market_price}/MWh`
                      : "Market: N/A",
                  ];
                }
              }
              return "";
            },
          },
        },
      },
      scales: {
        x: {
          title: {
            display: true,
            text: "Date",
          },
        },
        y: {
          title: {
            display: true,
            text: "Price (€/MWh)",
          },
          beginAtZero: false,
        },
      },
    },
  };
};

const renderChart = async () => {
  const config = buildChartData();

  if (!config) {
    chartData.value = null;
    destroyChart();
    return;
  }

  chartData.value = config;

  await nextTick();

  if (chartCanvas.value) {
    destroyChart();
    chartInstance.value = new Chart(chartCanvas.value, config);
  }
};

watch(
  () => [props.marketPrices, props.transactions],
  () => {
    renderChart();
  },
  { deep: true }
);

onMounted(() => {
  renderChart();
});

onBeforeUnmount(() => {
  destroyChart();
});
</script>

<style scoped>
.chart-container {
  position: relative;
  height: 500px;
  width: 100%;
}

canvas {
  max-height: 100%;
}

.chart-loading-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  border-radius: 0.5rem;
  background-color: var(--phoenix-gray-1000);
  color: var(--phoenix-gray-400);
  font-size: 0.875rem;
}
</style>
